import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button, Stack, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import swal from 'sweetalert2'
import { useMe } from '../../../hooks/useMe'
import { useSimpleForm } from '../../../hooks/useSimpleForm'
import { updateParcel } from '../../../redux/actions'
import {
  sendResetPhoneEmail,
  sendParcelEmail,
} from '../../../redux/parcels/async.parcelActions'
import showResponseText, { showProblem } from '../showResponseText'
import Fact from '../Fact'

// iBoxen edits in any status; other carriers keep the delivery service's limits
const CARRIER_EDITABLE_STATUSES = [
  'created',
  'on-way',
  'pickup-on-way',
  'in-box',
]

// The delivery service only sends the change-your-number email for these two, and answers 400
// for anything else, so the button is not offered where it cannot work.
const RESET_PHONE_STATUSES = ['in-box', 'on-way']

// The registry shows this for every parcel; the data decides. The part renders
// when the parcel has a sender row or a consumer, not when it merely has an
// editor to offer. The sender row below is iBoxen-only and the registry cannot
// say so (the consumer is shown to every carrier), so that gate stays here.
export default function Identity({ parcel, onChanged }) {
  const { t } = useTranslation('parcels')
  const { t: transCommon } = useTranslation('common')
  const { t: transResponseMessage } = useTranslation('responseMessages')
  const dispatch = useDispatch()
  const { carrier, can } = useMe()
  const [editing, setEditing] = useState(false)
  const [sending, setSending] = useState(false)

  // a pickup parcel's consumer is the person who hands the parcel in
  const consumerKey = parcel.flow === 'pickup' ? 'sender' : 'receiver'
  const consumer = parcel[consumerKey]
  const consumerForm = useSimpleForm({
    fields: {
      fullName: consumer?.fullName || '',
      phoneNumber: consumer?.phoneNumber || '',
    },
    async onSubmit(_, updated) {
      // a throw (the request itself failing) is a failed save like an API error
      let failed
      try {
        const result = await dispatch(
          updateParcel(parcel._id, { [consumerKey]: updated })
        )
        failed = Boolean(result.error)
      } catch (e) {
        failed = true
      }
      if (failed) {
        consumerForm.reset()
      } else {
        await onChanged()
      }
      setEditing(false)
    },
  })

  const { fullName, phoneNumber } = consumerForm.fields
  const showSender =
    carrier.isIboxen &&
    Boolean(
      parcel.sender?.phoneNumber || parcel.sender?.fullName || parcel.senderName
    )
  const canEdit =
    can('updateParcelConsumerData') &&
    (carrier.isIboxen ||
      CARRIER_EDITABLE_STATUSES.includes(parcel.currentStatus))
  // Read-only: the fields the server lets anyone edit are the name and the number, so an email
  // box here would look editable and quietly do nothing.
  const email = consumer?.email

  // The email that lets the consumer change their own number. The carrier half of `can` is what
  // keeps it to the carriers that have it, and the server asks for the same permission again.
  const canResetPhone =
    can('sendResetPhoneEmail') &&
    RESET_PHONE_STATUSES.includes(parcel.currentStatus) &&
    Boolean(parcel.receiver?.email)

  // The server works out which of its templates this parcel's carrier has switched on and is in
  // the right status for, because neither is something the page could know. What is left to ask
  // here is whether this person may send one at all.
  const emailTemplates = can('sendConsumerEmail')
    ? parcel.actions?.emailTemplates ?? []
    : []

  const ask = async (text) =>
    (await swal.fire({ text, showCancelButton: true, showConfirmButton: true }))
      .isConfirmed

  const run = async (action) => {
    setSending(true)
    try {
      const { payload } = await dispatch(action)
      showResponseText(payload, transResponseMessage)
    } catch (e) {
      // the action shows its own error when the request failed; this is for anything around it
      showProblem(transResponseMessage)
    } finally {
      setSending(false)
      onChanged()
    }
  }

  const sendTemplate = async (template) => {
    if (!(await ask(t(`confirmEmail.${template}`)))) return
    await run(sendParcelEmail({ _id: parcel._id, template }))
  }

  const sendReset = async () => {
    if (!(await ask(t('confirmResetPhoneEmail')))) return
    await run(sendResetPhoneEmail({ _id: parcel._id }))
  }

  // from the parcel, not the form: the form is seeded once and only serves the editor
  const hasConsumer = Boolean(
    consumer?.fullName || consumer?.phoneNumber || email
  )
  if (!showSender && !hasConsumer && !editing) return null

  return (
    <Stack spacing={1}>
      {showSender && (
        <Fact label={t('sender')}>
          <Stack component="span">
            <span>{parcel.sender?.fullName || parcel.senderName}</span>
            {parcel.sender?.phoneNumber && (
              <span>{parcel.sender.phoneNumber}</span>
            )}
          </Stack>
        </Fact>
      )}
      {(hasConsumer || canEdit || editing) && (
        <Fact label={t('consumer')}>
          {editing ? (
            <Stack component="span" spacing={1}>
              <TextField
                size="small"
                hiddenLabel
                disabled={consumerForm.submitting}
                inputProps={{ 'aria-label': t('name') }}
                {...fullName}
              />
              <TextField
                size="small"
                hiddenLabel
                disabled={consumerForm.submitting}
                inputProps={{ 'aria-label': t('phone') }}
                {...phoneNumber}
              />
            </Stack>
          ) : (
            <Stack component="span">
              {fullName.value && <span>{fullName.value}</span>}
              {phoneNumber.value && <span>{phoneNumber.value}</span>}
              {email && <span>{email}</span>}
            </Stack>
          )}
        </Fact>
      )}
      {!editing && (canEdit || canResetPhone || emailTemplates.length > 0) && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {canEdit && (
            <Button
              variant="contained"
              color="iboxen"
              size="small"
              onClick={() => setEditing(true)}
            >
              {t('edit')}
            </Button>
          )}
          {emailTemplates.map((template) => (
            <Button
              key={template}
              variant="outlined"
              color="iboxen"
              size="small"
              disabled={sending}
              onClick={() => sendTemplate(template)}
            >
              {t(`sendEmail.${template}`)}
            </Button>
          ))}
          {canResetPhone && (
            <Button
              variant="outlined"
              color="iboxen"
              size="small"
              disabled={sending}
              onClick={sendReset}
            >
              {t('sendResetPhoneEmail')}
            </Button>
          )}
        </Stack>
      )}
      {editing && (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="iboxen"
            size="small"
            onClick={() => consumerForm.submit()}
            disabled={
              consumerForm.submitting || consumerForm.changed.length === 0
            }
          >
            {consumerForm.submitting
              ? transCommon('saving')
              : transCommon('save')}
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => {
              consumerForm.reset()
              setEditing(false)
            }}
            disabled={consumerForm.submitting}
          >
            {t('abort')}
          </Button>
        </Stack>
      )}
    </Stack>
  )
}
